import * as React from "react";
import { ChevronLeft } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { cn } from "@/lib/utils";

export interface ScreenHeaderProps {
  title?: React.ReactNode;
  /** Optional text shown next to the back chevron (e.g. "Anasayfa"). */
  backLabel?: React.ReactNode;
  onBack?: () => void;
  showBack?: boolean;
  /** Right-aligned actions (e.g. bell, more, "Paylaş"). */
  right?: React.ReactNode;
  className?: string;
  /** Optional content rendered as an extra row inside the navy bar. */
  children?: React.ReactNode;
}

/**
 * Navy (brand-900) sticky top bar used across redesigned screens.
 * The `<header>` element receives safe-area top padding from index.css.
 */
export const ScreenHeader: React.FC<ScreenHeaderProps> = ({
  title,
  backLabel,
  onBack,
  showBack = true,
  right,
  className,
  children,
}) => {
  const navigate = useNavigate();
  const handleBack = onBack ?? (() => navigate(-1));

  return (
    <header data-safe-top-header className={cn("sticky top-0 z-30 bg-brand-900 text-white", className)}>
      <div className="grid h-14 grid-cols-[1fr_auto_1fr] items-center gap-2 px-4">
        <div className="flex min-w-0 items-center justify-start">
          {showBack && (
            <button
              type="button"
              onClick={handleBack}
              aria-label="Geri"
              className="-ml-1 flex items-center gap-1 rounded-md py-1 pr-2 text-[15px] text-brand-25 transition-colors hover:text-white"
            >
              <ChevronLeft className="h-5 w-5 shrink-0" />
              {backLabel ? <span className="truncate">{backLabel}</span> : null}
            </button>
          )}
        </div>
        <div className="min-w-0 text-center">
          {typeof title === "string" ? (
            <h1 className="truncate text-[17px] font-bold">{title}</h1>
          ) : (
            title
          )}
        </div>
        <div className="flex items-center justify-end gap-3 text-brand-25">{right}</div>
      </div>
      {children}
    </header>
  );
};

export default ScreenHeader;
